import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';
import RestoreIcon from '@material-ui/icons/Restore';
import BlockIcon from '@material-ui/icons/Block';
import { TRACKERS, MONTH_LABELS } from './config/trackers';
import {
    exportAccountsCSV,
    exportPaymentsCSV,
    printAccounts,
    printPayments,
    printSingleAccount,
} from './utils/print';
import { BELONGS_KEYS, BELONGS_META } from './config/belongs';
import {
    parseCell,
    getCellStatus,
    getCellTotalPaid,
    getCellTotalSales,
    isCellFullyVerified,
    parsePaymentMethod,
    isQuarterlyFilingMonth,
    cellsForYear,
    formatMoney,
} from './utils/cells';
import { totalPaidForAccount, pickMonthlyEstimate } from './utils/derive';
import PaymentPopup from './PaymentPopup';
import LicensesView from './LicensesView';
import MaintenanceView from './MaintenanceView';

const TABS = { ACCOUNTS: 'accounts', PAYMENTS: 'payments' };

const SUB_TAX_ICONS = {
    propertyTax: ['🏛️', '🎓', '💧', '🏙️', '🏥'],
    annualTax: ['📋', '🇺🇸', '⚖️', '📑', '📊'],
};

/**
 * Standard tracker detail page (Accounts | Payments).
 *
 * Annual trackers (propertyTax, annualTax) render a 5-slot subTaxes grid
 * instead of months[]. tenantRent flips colors via incoming=true. salesTax
 * pipes the dual-field sales/tax values through PaymentPopup.
 */
function TrackerDetail({
    trackerKey,
    initialBelongsFilter,
    accounts,
    api,
    showToast,
    onOpenAddAccount,
}) {
    const tracker = TRACKERS[trackerKey];
    const initialTab = tracker.hideAccountsTab ? TABS.PAYMENTS : TABS.ACCOUNTS;
    const [tab, setTab] = useState(initialTab);
    const [search, setSearch] = useState('');
    const [belongsFilter, setBelongsFilter] = useState(initialBelongsFilter || 'all');
    const [paymentTarget, setPaymentTarget] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear());

    const rows = useMemo(
        () => accounts
            // Deleted rows are soft-deleted server-side and never returned by
            // listAll; this guard keeps them out even if one lingers in state.
            .filter((a) => a.trackerKey === trackerKey && a.status !== 'deleted')
            .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
        [accounts, trackerKey],
    );
    const visibleRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        return rows.filter((r) => {
            if (r.status !== 'active' && tab === TABS.PAYMENTS) return false;
            if (belongsFilter !== 'all' && r.belongs !== belongsFilter) return false;
            if (!term) return true;
            const haystack = JSON.stringify({ label: r.label, fields: r.fields }).toLowerCase();
            return haystack.includes(term);
        });
    }, [rows, search, belongsFilter, tab]);

    return (
        <>
            {/* Page header — title + subtitle (back button lives in the toolbar). */}
            <div className="bt-tracker-title">
                <h1>
                    <span role="img" aria-label={tracker.label}>{tracker.icon}</span>{' '}
                    {tracker.label}
                </h1>
                <p>{trackerSubtitle(tracker)}</p>
            </div>

            {/* Big primary CTA */}
            <button
                className="bt-btn-primary"
                type="button"
                onClick={() => onOpenAddAccount(trackerKey, null)}
            >
                {tracker.addButtonLabel
                    || `+ Add ${tracker.label === 'Tenant Rent' ? 'Tenant' : 'Account'}`}
            </button>

            {/* Tabs — hidden when the tracker only has a custom Orders/Renewals view. */}
            {!tracker.hideAccountsTab && (
                <div className="bt-tabs">
                    <button
                        className={`bt-tab ${tab === TABS.ACCOUNTS ? 'active' : ''}`}
                        onClick={() => setTab(TABS.ACCOUNTS)}
                        type="button"
                    >
                        Accounts
                    </button>
                    <button
                        className={`bt-tab ${tab === TABS.PAYMENTS ? 'active' : ''}`}
                        onClick={() => setTab(TABS.PAYMENTS)}
                        type="button"
                    >
                        {tracker.paymentsTabLabel || 'Payments'}
                    </button>
                </div>
            )}

            {/* Search + belongs filter (Accounts tab uses these) */}
            {tab === TABS.ACCOUNTS && (
                <div className="bt-filter-card">
                    <div className="bt-search-input">
                        <span role="img" aria-label="Search">🔍</span>
                        <input
                            type="text"
                            placeholder="Search location, account, vendor..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        className="bt-dropdown"
                        value={belongsFilter}
                        onChange={(e) => setBelongsFilter(e.target.value)}
                    >
                        <option value="all">✓ All</option>
                        {BELONGS_KEYS.map((k) => (
                            <option key={k} value={k}>
                                {BELONGS_META[k].icon} {BELONGS_META[k].label}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {tab === TABS.ACCOUNTS && (
                <AccountsTab
                    tracker={tracker}
                    rows={visibleRows}
                    filterLabel={belongsFilterLabel(belongsFilter)}
                    onEdit={(row) => onOpenAddAccount(trackerKey, row)}
                    onCancel={(row) => {
                        if (row.status === 'cancelled') {
                            api.saveAccount(
                                { ...accountToPayload(row), status: 'active' },
                                () => {},
                            );
                            showToast('Reactivated', 'success');
                        } else {
                            api.cancelAccount(row.accountId);
                        }
                    }}
                    onDelete={(row) => api.deleteAccount(row.accountId)}
                    onPrintRow={(row) => {
                        printSingleAccount(tracker, row);
                        showToast('Statement opened in new tab', 'success');
                    }}
                    onPrintAll={() => {
                        printAccounts(tracker, visibleRows, belongsFilterLabel(belongsFilter));
                        showToast('Accounts report opened in new tab', 'success');
                    }}
                    onExportCSV={() => {
                        const f = exportAccountsCSV(tracker, visibleRows);
                        showToast(`Downloaded ${f}`, 'success');
                    }}
                />
            )}

            {tab === TABS.PAYMENTS && tracker.customView === 'licenses' && (
                <LicensesView
                    accounts={rows}
                    api={api}
                    showToast={showToast}
                />
            )}

            {tab === TABS.PAYMENTS && tracker.customView === 'maintenance' && (
                <MaintenanceView
                    accounts={rows}
                    api={api}
                    showToast={showToast}
                />
            )}

            {tab === TABS.PAYMENTS && !tracker.customView && (
                <PaymentsTab
                    tracker={tracker}
                    rows={visibleRows}
                    year={year}
                    onYearChange={setYear}
                    filterLabel={belongsFilterLabel(belongsFilter)}
                    onPrint={() => {
                        printPayments(tracker, visibleRows, year, belongsFilterLabel(belongsFilter));
                        showToast('Payments report opened in new tab', 'success');
                    }}
                    onExportCSV={() => {
                        const f = exportPaymentsCSV(tracker, visibleRows, year);
                        showToast(`Downloaded ${f}`, 'success');
                    }}
                    onCellClick={(row, slot, index) => setPaymentTarget({ row, slot, index })}
                />
            )}

            {paymentTarget && (
                <PaymentPopup
                    tracker={tracker}
                    target={paymentTarget}
                    year={year}
                    onClose={() => setPaymentTarget(null)}
                    onSave={(cellValue) => {
                        api.saveCell(
                            {
                                accountId: paymentTarget.row.accountId,
                                cellIndex: paymentTarget.index,
                                slot: paymentTarget.slot,
                                year,
                                cellValue,
                            },
                            (err) => {
                                if (!err) setPaymentTarget(null);
                            },
                        );
                    }}
                />
            )}
        </>
    );
}

function trackerSubtitle(tracker) {
    switch (tracker.key) {
        case 'electricity': return 'All electricity accounts across your locations';
        case 'dumpster':    return 'Waste & recycling vendors per location';
        case 'insurance':   return 'Active insurance policies';
        case 'alarm':       return 'Alarm and monitoring services';
        case 'pest':        return 'Pest control vendors and contracts';
        case 'mortgage':    return 'Mortgages and rent obligations by property';
        case 'tenantRent':  return 'Incoming tenant rent across rental properties';
        case 'salesTax':    return 'Sales & use tax filings by entity';
        case 'propertyTax': return 'Annual property tax obligations by property';
        case 'annualTax':   return 'Franchise & federal income tax by entity';
        case 'licenses':    return 'Recurring licenses & permits by location';
        case 'maintenance': return 'Repair & maintenance work orders';
        default:            return 'Accounts overview';
    }
}

// ----------------------------------------------------------------------
// Accounts tab — table with floating action menu.
// ----------------------------------------------------------------------
function AccountsTab({ tracker, rows, onEdit, onCancel, onDelete, onPrintRow, onPrintAll, onExportCSV }) {
    // Pending confirmation: { row, mode: 'cancel' | 'delete' } or null.
    // Reactivating a cancelled row is non-destructive, so it runs immediately;
    // Cancel and Delete are both confirmed here.
    const [confirm, setConfirm] = useState(null);

    const requestCancel = (row) => {
        if (row.status === 'cancelled') {
            onCancel(row); // reactivate — no confirmation needed
        } else {
            setConfirm({ row, mode: 'cancel' });
        }
    };
    const requestDelete = (row) => setConfirm({ row, mode: 'delete' });

    return (
        <>
        <div className="bt-tbl-card">
            <div className="bt-tbl-card-head">
                <div className="bt-tbl-card-head-left">
                    <h3>{tracker.label} Accounts</h3>
                    <span className="bt-tbl-card-count">
                        {rows.length} record{rows.length === 1 ? '' : 's'}
                    </span>
                </div>
                <ExportMenu
                    disabled={rows.length === 0}
                    onPrint={onPrintAll}
                    onCSV={onExportCSV}
                />
            </div>
            {rows.length === 0 ? (
                <div className="bt-empty">
                    No accounts yet. Click <strong>+ Add Account</strong> to create one.
                </div>
            ) : (
                <div className="bt-tbl-scroll">
                    <table>
                        <thead>
                            <tr>
                                {tracker.cols.map((c) => (
                                    <th key={c}>{c}</th>
                                ))}
                                <th style={{ width: 60, textAlign: 'right' }}> </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr
                                    key={row.accountId}
                                    className={row.status === 'cancelled' ? 'bt-row-cancelled' : ''}
                                >
                                    {renderRowCells(tracker, row)}
                                    <td style={{ textAlign: 'right' }}>
                                        <RowActionMenu
                                            row={row}
                                            onEdit={() => onEdit(row)}
                                            onPrint={() => onPrintRow(row)}
                                            onCancel={() => requestCancel(row)}
                                            onDelete={() => requestDelete(row)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
        {confirm && (
            <ConfirmActionDialog
                row={confirm.row}
                mode={confirm.mode}
                onClose={() => setConfirm(null)}
                onConfirm={() => {
                    if (confirm.mode === 'delete') onDelete(confirm.row);
                    else onCancel(confirm.row);
                    setConfirm(null);
                }}
            />
        )}
        </>
    );
}

/**
 * Confirmation dialog for the two destructive row actions — portaled overlay
 * matching the tracker's "silk" modal design system.
 *
 *   mode='cancel' — reversible: marks the account cancelled (hidden from the
 *                   Payments tab) but it stays listed and can be reactivated.
 *   mode='delete' — soft-delete: removes it from the trackers. The record is
 *                   retained server-side for audit but can't be restored here.
 */
function ConfirmActionDialog({ row, mode, onClose, onConfirm }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Enter') onConfirm();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, onConfirm]);

    const isDelete = mode === 'delete';
    const Icon = isDelete ? DeleteOutlineIcon : BlockIcon;
    const title = isDelete ? 'Delete this account?' : 'Cancel this account?';
    const confirmLabel = isDelete ? 'Delete' : 'Cancel';

    return ReactDOM.createPortal(
        // bt-root re-declares the --bt-* design tokens on this element so
        // they resolve inside the portal (which lives outside .bt-root).
        <div
            className="bt-root bt-overlay bt-overlay--center"
            role="presentation"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="bt-modal bt-modal--confirm"
                role="dialog"
                aria-modal="true"
                aria-labelledby="bt-confirm-title"
                style={{ maxWidth: 420 }}
            >
                <div className="bt-pp-confirm-wrap">
                    <div
                        style={{
                            width: 56,
                            height: 56,
                            margin: '0 auto 12px',
                            borderRadius: '50%',
                            background: '#FDECEA',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Icon style={{ color: '#C0392B', fontSize: 30 }} />
                    </div>
                    <div className="bt-pp-confirm-title" id="bt-confirm-title">
                        {title}
                    </div>
                    <div className="bt-pp-confirm-text">
                        {isDelete ? (
                            <>
                                <strong>{row.label || '(unnamed)'}</strong> will be{' '}
                                <strong>removed</strong> from your trackers. The record is
                                archived for audit, but this can&apos;t be undone from here.
                            </>
                        ) : (
                            <>
                                <strong>{row.label || '(unnamed)'}</strong> will be marked as{' '}
                                <strong>cancelled</strong> and hidden from the Payments tab. Its
                                payment history is preserved and you can reactivate it any time.
                            </>
                        )}
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        gap: 10,
                        marginTop: 20,
                    }}
                >
                    <ConfirmButton variant="ghost" onClick={onClose}>
                        Keep
                    </ConfirmButton>
                    <ConfirmButton variant="danger" onClick={onConfirm}>
                        <Icon style={{ fontSize: 18, marginRight: 6 }} />
                        {confirmLabel}
                    </ConfirmButton>
                </div>
            </div>
        </div>,
        document.body,
    );
}

/**
 * Dialog action button — fully self-contained styling (inline + JS hover)
 * so it renders consistently inside the portaled overlay, independent of
 * the app's global button resets. 'ghost' = secondary, 'danger' = solid red.
 */
function ConfirmButton({ variant, onClick, children }) {
    const [hover, setHover] = useState(false);
    const isDanger = variant === 'danger';

    const base = {
        flex: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px 16px',
        borderRadius: 12,
        fontFamily: 'inherit',
        fontSize: 15,
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'background .15s, box-shadow .15s, border-color .15s',
        outline: 'none',
        whiteSpace: 'nowrap',
    };

    // The dialog is portaled to document.body, where the tracker's CSS
    // custom properties (--bt-red, etc.) are out of scope — so use literal
    // colors here instead of var() references.
    const skin = isDanger
        ? {
            // Constant strong-red look — no hover color change.
            background: '#C0392B',
            color: '#fff',
            border: '1px solid transparent',
            boxShadow: '0 4px 14px rgba(192, 57, 43, 0.32)',
        }
        : {
            background: hover ? '#f3f4f6' : '#fff',
            color: '#1e293b',
            border: '1px solid #e5e7eb',
            boxShadow: 'none',
        };

    return (
        <button
            type="button"
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{ ...base, ...skin }}
        >
            {children}
        </button>
    );
}

/**
 * Export menu — small dropdown next to the table card head with Print
 * and CSV options. Portaled to <body> to escape the table's overflow.
 */
function ExportMenu({ disabled, onPrint, onCSV }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    // Same flip-above-when-clipped positioning as RowActionMenu.
    useLayoutEffect(() => {
        if (!open) { setPos(null); return; }
        const trigger = triggerRef.current?.getBoundingClientRect();
        const menu = menuRef.current?.getBoundingClientRect();
        if (!trigger || !menu) return;
        const margin = 8;
        let top = trigger.bottom + 4;
        if (top + menu.height > window.innerHeight - margin) {
            top = trigger.top - menu.height - 4;
        }
        if (top < margin) top = margin;
        setPos({ top, right: window.innerWidth - trigger.right });
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            const inT = triggerRef.current && triggerRef.current.contains(e.target);
            const inM = menuRef.current && menuRef.current.contains(e.target);
            if (!inT && !inM) setOpen(false);
        };
        const close = () => setOpen(false);
        document.addEventListener('mousedown', handler);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', handler);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [open]);

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className="bt-btn-export"
                onClick={() => setOpen((v) => !v)}
                disabled={disabled}
            >
                <span role="img" aria-label="Export">⬇️</span> Export
            </button>
            {open && ReactDOM.createPortal(
                <div
                    ref={menuRef}
                    className="bt-action-menu"
                    role="menu"
                    style={{
                        position: 'fixed',
                        top: pos ? pos.top : -9999,
                        right: pos ? pos.right : 0,
                        visibility: pos ? 'visible' : 'hidden',
                    }}
                >
                    <button type="button" onClick={() => { setOpen(false); onPrint(); }}>
                        <span role="img" aria-label="Print">🖨️</span> Print / Save as PDF
                    </button>
                    <button type="button" onClick={() => { setOpen(false); onCSV(); }}>
                        <span role="img" aria-label="Download">📊</span> Download CSV
                    </button>
                </div>,
                document.body,
            )}
        </>
    );
}

/**
 * Floating row action menu (edit / print / cancel-reactivate / delete).
 *
 * Cancel is reversible (toggles to Reactivate on cancelled rows); Delete is
 * the soft-delete that removes the row from view. The menu is portaled to
 * <body> and positioned with viewport coords, so it escapes the table's
 * overflow:hidden / overflow-x:auto clipping. The bounding rect is captured
 * when the trigger is clicked; the menu opens just below the trigger,
 * right-aligned to it.
 */
function RowActionMenu({ row, onEdit, onPrint, onCancel, onDelete }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null); // { top, right } once measured
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    // Position the portaled menu under its trigger — but flip it ABOVE when it
    // would overflow the bottom of the viewport (the reported bug: the menu on
    // the last on-screen row was clipped). Measured post-mount so the flip is
    // based on the menu's real height, then clamped to the top edge too.
    useLayoutEffect(() => {
        if (!open) { setPos(null); return; }
        const trigger = triggerRef.current?.getBoundingClientRect();
        const menu = menuRef.current?.getBoundingClientRect();
        if (!trigger || !menu) return;
        const margin = 8;
        let top = trigger.bottom + 4;
        if (top + menu.height > window.innerHeight - margin) {
            top = trigger.top - menu.height - 4; // not enough room below → open upward
        }
        if (top < margin) top = margin; // never spill past the top edge
        setPos({ top, right: window.innerWidth - trigger.right });
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            const inTrigger = triggerRef.current && triggerRef.current.contains(e.target);
            const inMenu = menuRef.current && menuRef.current.contains(e.target);
            if (!inTrigger && !inMenu) setOpen(false);
        };
        const onScrollOrResize = () => setOpen(false);
        document.addEventListener('mousedown', handler);
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            document.removeEventListener('mousedown', handler);
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [open]);

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className="bt-action-btn"
                onClick={() => setOpen((v) => !v)}
                aria-label="Row actions"
            >
                ⋯
            </button>
            {open && ReactDOM.createPortal(
                <div
                    ref={menuRef}
                    className="bt-action-menu"
                    role="menu"
                    style={{
                        position: 'fixed',
                        top: pos ? pos.top : -9999,
                        right: pos ? pos.right : 0,
                        // Hidden for the single measuring frame to avoid a flash
                        // at the pre-flip position.
                        visibility: pos ? 'visible' : 'hidden',
                    }}
                >
                    <button type="button" onClick={() => { setOpen(false); onEdit(); }}>
                        <EditOutlinedIcon /> Edit
                    </button>
                    <button type="button" onClick={() => { setOpen(false); onPrint(); }}>
                        <PrintOutlinedIcon /> Print Payment History
                    </button>
                    <button
                        type="button"
                        onClick={() => { setOpen(false); onCancel(); }}
                    >
                        {row.status === 'cancelled' ? (
                            <><RestoreIcon /> Reactivate</>
                        ) : (
                            <><BlockIcon /> Cancel</>
                        )}
                    </button>
                    <button
                        type="button"
                        className="danger"
                        onClick={() => { setOpen(false); onDelete(); }}
                    >
                        <DeleteOutlineIcon /> Delete
                    </button>
                </div>,
                document.body,
            )}
        </>
    );
}

/**
 * Map a row → table cells per the columns declared in TRACKERS[k].cols.
 * Field names match the v4 trackers.js config (address, accountName,
 * provider, etc.).
 */
function renderRowCells(tracker, row) {
    const f = row.fields || {};
    const statusPill = (
        <span
            className={`bt-status-pill ${row.status === 'cancelled' ? 'cancelled' : 'active'}`}
        >
            {row.status === 'cancelled' ? 'Cancelled' : 'Active'}
        </span>
    );

    const cell = (value, money = false) => (
        <td className={money ? 'bt-cell-money' : ''}>
            {value || <span className="bt-cell-muted">—</span>}
        </td>
    );

    // If a dropdown was answered with "Other", swap in the user-typed value
    // from the matching `<name>Other` field instead of the literal "Other".
    const resolve = (name) => {
        const v = f[name];
        if (v === 'Other' && f[`${name}Other`]) return f[`${name}Other`];
        return v;
    };

    const ytd = formatMoney(totalPaidForAccount(row));

    switch (tracker.key) {
        case 'electricity':
            return (
                <>
                    {cell(row.label)}
                    {cell(f.address)}
                    {cell(f.accountName)}
                    {cell(resolve('provider'))}
                    {cell(f.accountNumber)}
                    {cell(f.ratePerKwh)}
                    {cell(f.contractEnd)}
                    {cell(f.paymentMethod)}
                    <td>{statusPill}</td>
                </>
            );
        case 'dumpster':
            return (
                <>
                    {cell(row.label)}
                    {cell(f.address)}
                    {cell(f.accountName)}
                    {cell(resolve('vendor'))}
                    {cell(f.accountNumber)}
                    {cell(f.planSize)}
                    {cell(f.monthlyCost, true)}
                    {cell(f.contractEnd)}
                    {cell(f.paymentMethod)}
                    <td>{statusPill}</td>
                </>
            );
        case 'insurance':
            return (
                <>
                    {cell(row.label)}
                    {cell(f.address)}
                    {cell(f.insuranceCompany)}
                    {cell(f.policyNumber)}
                    {cell(resolve('policyType'))}
                    {cell(f.termLength)}
                    {cell(f.totalPremium, true)}
                    {cell(f.expirationDate)}
                    {cell(f.paymentMethod)}
                    <td>{statusPill}</td>
                </>
            );
        case 'alarm':
            return (
                <>
                    {cell(row.label)}
                    {cell(f.address)}
                    {cell(f.accountName)}
                    {cell(resolve('alarmCompany'))}
                    {cell(f.accountNumber)}
                    {cell(f.planService)}
                    {cell(f.monthlyCost, true)}
                    {cell(f.contractEnd)}
                    {cell(f.paymentMethod)}
                    <td>{statusPill}</td>
                </>
            );
        case 'pest':
            return (
                <>
                    {cell(row.label)}
                    {cell(f.address)}
                    {cell(f.accountName)}
                    {cell(resolve('provider'))}
                    {cell(f.accountNumber)}
                    {cell(f.serviceFrequency)}
                    {cell(f.costPerService, true)}
                    {cell(f.contractEnd)}
                    {cell(f.paymentMethod)}
                    <td>{statusPill}</td>
                </>
            );
        case 'mortgage':
            return (
                <>
                    {cell(row.label)}
                    {cell(f.address)}
                    {cell(f.mortgageType)}
                    {cell(f.lender)}
                    {cell(f.accountNumber)}
                    {cell(f.monthlyPayment, true)}
                    {cell(f.mortgageType === 'Rent' ? f.leaseEndDate : f.originalLoanAmount)}
                    {cell(f.paymentMethod)}
                    <td>{statusPill}</td>
                </>
            );
        case 'tenantRent':
            return (
                <>
                    {cell(row.label)}
                    {cell(f.tenantName)}
                    {cell(f.leaseStart && f.leaseEnd ? `${f.leaseStart} → ${f.leaseEnd}` : (f.leaseEnd || f.leaseStart))}
                    {cell(f.monthlyRent, true)}
                    {cell(f.securityDeposit, true)}
                    {cell(f.lateFeeAmount && f.lateFeeAfterDay
                        ? `${f.lateFeeAmount} after day ${f.lateFeeAfterDay}`
                        : f.lateFeeAmount)}
                    {cell(ytd, true)}
                    <td>{statusPill}</td>
                </>
            );
        case 'salesTax':
            return (
                <>
                    {cell(row.label)}
                    {cell(f.address)}
                    {cell(f.filingFrequency)}
                    {cell(f.permitNumber)}
                    {cell(resolve('filingMethod'))}
                    {cell(f.lastFiledDate)}
                    {cell(f.nextDueDate)}
                    {cell(f.paymentMethod)}
                    <td>{statusPill}</td>
                </>
            );
        case 'propertyTax':
            return (
                <>
                    {cell(row.label)}
                    {cell(f.address)}
                    {cell(resolve('county'))}
                    {cell(f.schoolDistrict)}
                    {cell(f.appraisedValue, true)}
                    {cell(f.homestead)}
                    {cell(ytd, true)}
                    {cell(f.paymentMethod)}
                    <td>{statusPill}</td>
                </>
            );
        case 'annualTax':
            return (
                <>
                    {cell(row.label)}
                    {cell(resolve('parentCompany'))}
                    {cell(f.ownership)}
                    {cell(f.entityType)}
                    {cell(f.ein)}
                    {cell(f.annualizedRevenue)}
                    {cell(f.franchiseFiling)}
                    {cell(resolve('federalForm'))}
                    <td>{statusPill}</td>
                </>
            );
        case 'licenses':
            return (
                <>
                    {cell(row.label)}
                    {cell(f.permitType)}
                    {cell(f.authority)}
                    {cell(f.permitNumber)}
                    {cell(f.issued)}
                    {cell(f.expires)}
                    {cell(f.renewalCost, true)}
                    {cell(f.frequency)}
                    <td>{statusPill}</td>
                </>
            );
        case 'maintenance':
            return (
                <>
                    {cell(row.label)}
                    {cell(f.orderLocation)}
                    {cell(resolve('category'))}
                    {cell(f.priority)}
                    {cell(f.vendor)}
                    {cell(f.quotedCost, true)}
                    {cell(f.actualCost, true)}
                    {cell(f.dateRequested)}
                    <td>{statusPill}</td>
                </>
            );
        default:
            return tracker.cols.map((_, i) => <td key={i}>—</td>);
    }
}

// ----------------------------------------------------------------------
// Payments tab — year nav, legend, summary strip, location accordion list.
// ----------------------------------------------------------------------
function PaymentsTab({ tracker, rows, year, onYearChange, onCellClick, onPrint, onExportCSV }) {
    const [openId, setOpenId] = useState(null);

    const stats = useMemo(() => buildPaymentsStats(tracker, rows, year), [tracker, rows, year]);

    if (rows.length === 0) {
        return (
            <div className="bt-empty">
                No active accounts to track. Add one in the Accounts tab.
            </div>
        );
    }

    return (
        <>
            {/* Year nav with Print + CSV controls on the right */}
            <div className="bt-year-nav">
                <button
                    type="button"
                    onClick={() => onYearChange(year - 1)}
                    aria-label="Previous year"
                >
                    ‹
                </button>
                <span className="year">{year}</span>
                <button
                    type="button"
                    onClick={() => onYearChange(year + 1)}
                    aria-label="Next year"
                >
                    ›
                </button>
            </div>

            {/* Export controls — Print full-year report and download CSV */}
            <div className="bt-export-row">
                <button type="button" className="bt-btn-export" onClick={onPrint}>
                    <span role="img" aria-label="Print">🖨️</span> Print / Save as PDF
                </button>
                <button type="button" className="bt-btn-export" onClick={onExportCSV}>
                    <span role="img" aria-label="Download">📊</span> Download CSV
                </button>
            </div>

            {/* Legend */}
            <div className="bt-legend" aria-hidden="true">
                <div className="bt-legend-item">
                    <span className="bt-legend-swatch verified" /> Verified
                </div>
                <div className="bt-legend-item">
                    <span className="bt-legend-swatch paid" /> Paid
                </div>
                <div className="bt-legend-item">
                    <span className="bt-legend-swatch unverified" /> Unverified
                </div>
                <div className="bt-legend-item">
                    <span className="bt-legend-swatch pending" /> Pending
                </div>
                <div className="bt-legend-item">
                    <span className="bt-legend-swatch overdue" /> Overdue
                </div>
                <div className="bt-legend-item">
                    <span className="bt-legend-swatch future" /> Upcoming
                </div>
            </div>

            {/* 6-stat summary strip */}
            <div className="bt-payments-summary">
                <div className="bt-summary-stat paid">
                    <div className="bt-summary-stat-label">Paid</div>
                    <div className="bt-summary-stat-value">{stats.paid}</div>
                </div>
                <div className="bt-summary-stat verified">
                    <div className="bt-summary-stat-label">
                        <span role="img" aria-label="Verified">✓✓</span> Verified
                    </div>
                    <div className="bt-summary-stat-value">{stats.verified}</div>
                </div>
                <div className="bt-summary-stat unverified">
                    <div className="bt-summary-stat-label">
                        <span role="img" aria-label="Unverified">⏱</span> Unverified
                    </div>
                    <div className="bt-summary-stat-value">{stats.unverified}</div>
                </div>
                <div className="bt-summary-stat pending">
                    <div className="bt-summary-stat-label">Pending</div>
                    <div className="bt-summary-stat-value">{stats.pending}</div>
                </div>
                <div className="bt-summary-stat future">
                    <div className="bt-summary-stat-label">Upcoming</div>
                    <div className="bt-summary-stat-value">{stats.upcoming}</div>
                </div>
                <div className="bt-summary-stat total">
                    <div className="bt-summary-stat-label">
                        {rows.length} Location{rows.length === 1 ? '' : 's'}
                    </div>
                    <div className="bt-summary-stat-money">{formatMoney(stats.ytd)}</div>
                </div>
            </div>

            {/* Tip box for annual filings */}
            {tracker.key === 'propertyTax' && (
                <div className="bt-protest-tip">
                    <span role="img" aria-label="Tip">💡</span>{' '}
                    TX property tax protest deadline: <strong>May 15</strong>. Annual taxes due <strong>Jan 31</strong> next year.
                </div>
            )}
            {tracker.key === 'annualTax' && (
                <div className="bt-protest-tip">
                    <span role="img" aria-label="Tip">💡</span>{' '}
                    TX Franchise Tax due <strong>May 15</strong>. Federal Income Tax due <strong>Mar 16</strong> (Partnerships/S-Corps) or <strong>Apr 15</strong> (Sole Prop / C-Corp). Below $2.65M revenue = PIR only, no tax owed.
                </div>
            )}

            {/* Location accordion list */}
            <div className="bt-loc-list" style={{ marginTop: 12 }}>
                {rows.map((row, ri) => (
                    <LocationCard
                        key={row.accountId}
                        index={ri}
                        tracker={tracker}
                        row={row}
                        year={year}
                        open={openId === row.accountId}
                        onToggle={() => setOpenId(openId === row.accountId ? null : row.accountId)}
                        onCellClick={onCellClick}
                    />
                ))}
            </div>
        </>
    );
}

function LocationCard({ index, tracker, row, year, open, onToggle, onCellClick }) {
    const isAnnual = tracker.frequency === 'annual';
    const isQuarterly = tracker.key === 'salesTax' && row.fields?.filingFrequency === 'Quarterly';
    const incoming = !!row.incoming;
    const cells = cellsForYear(row, year, isAnnual ? 'subTaxes' : 'months');
    // YTD reflects the selected year's grid, not the flat current-year mirror.
    const ytd = cells.reduce((sum, c) => sum + getCellTotalPaid(parseCell(c)), 0);
    const monthlyEst = pickMonthlyEstimate(row);
    const method = parsePaymentMethod(row.paymentMethod);
    const operatorOnly = tracker.key === 'annualTax'
        && cells.every((c) => c === 'na' || !c);

    let overdue = 0;
    let pending = 0;
    let unverifiedCount = 0;
    let nextDueIdx = -1;
    cells.forEach((c, i) => {
        const parsed = parseCell(c);
        const status = effectiveStatus(parsed, isQuarterly, i);
        if (status === 'overdue') overdue += 1;
        else if (status === 'pending') pending += 1;
        else if (status === 'paid' || status === 'partial') {
            if (!isCellFullyVerified(parsed)) unverifiedCount += 1;
        }
        if (nextDueIdx === -1 && (status === 'pending' || status === 'overdue' || status === 'future')) {
            nextDueIdx = i;
        }
    });
    const nextDueLabel = nextDueIdx >= 0
        ? (isAnnual
            ? subTaxLabel(tracker, row, nextDueIdx)
            : `${MONTH_LABELS[nextDueIdx]} · ${formatMoney(monthlyEst)}`)
        : '—';

    // Build meta tags
    const metaTags = [];
    if (operatorOnly) {
        metaTags.push({ cls: 'warn', text: 'Operator only · not owned' });
    } else if (method.isAuto) {
        metaTags.push({
            cls: 'auto',
            text: <><span role="img" aria-label="Auto-pay">🔄</span> {method.method} · day {method.day}</>,
        });
    } else {
        metaTags.push({ cls: '', text: 'Manual' });
    }
    if (overdue > 0) metaTags.push({ cls: 'alert', text: `${overdue} overdue` });
    if (unverifiedCount > 0 && overdue === 0) {
        metaTags.push({
            cls: 'warn',
            text: <><span role="img" aria-label="Unverified">⏱</span> {unverifiedCount} unverified</>,
        });
    }
    if (pending > 0 && overdue === 0 && unverifiedCount === 0) {
        metaTags.push({ cls: 'warn', text: `${pending} due` });
    }

    return (
        <div className={`bt-loc-card${open ? ' expanded' : ''}`}>
            <div
                className="bt-loc-head"
                onClick={onToggle}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onToggle()}
            >
                <div className="bt-loc-num">#{index + 1}</div>
                <div className="bt-loc-info">
                    <div className="bt-loc-name">{row.label || '(unnamed)'}</div>
                    <div className="bt-loc-meta">
                        {metaTags.map((t, i) => (
                            <span key={i} className={`bt-tag ${t.cls}`}>{t.text}</span>
                        ))}
                    </div>
                </div>
                <div>
                    <div className="bt-loc-amt-label">YTD</div>
                    <div className="bt-loc-amt">{formatMoney(ytd)}</div>
                </div>
                <div className="bt-loc-chevron" aria-hidden="true">▼</div>
            </div>

            {/* 12 thin colored bars (only for monthly trackers) */}
            {!isAnnual && (
                <div className="bt-loc-strip" aria-hidden="true">
                    {cells.map((c, i) => {
                        const parsed = parseCell(c);
                        const status = effectiveStatus(parsed, isQuarterly, i);
                        const verified = status === 'paid' && isCellFullyVerified(parsed);
                        const cls = verified ? 'verified' : status;
                        return <span key={i} className={`bt-loc-strip-cell ${cls}`} />;
                    })}
                </div>
            )}

            {open && (
                <div className="bt-loc-body">
                    {operatorOnly ? (
                        <>
                            <div className="bt-operator-only">
                                <div className="bt-operator-icon"><span role="img" aria-label="Operator">👥</span></div>
                                <div className="bt-operator-title">Operator role only</div>
                                <div className="bt-operator-text">
                                    This entity is not owned by us — we operate it on behalf of the actual owner. No filing obligation on our end. The owning entity files its own taxes separately.
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Mini stat pills */}
                            <div className="bt-loc-stats">
                                <div className="bt-mini-stat paid">
                                    <div className="bt-mini-stat-label">
                                        {incoming ? 'Collected YTD' : 'Paid YTD'}
                                    </div>
                                    <div className="bt-mini-stat-value">{formatMoney(ytd)}</div>
                                </div>
                                <div className="bt-mini-stat">
                                    <div className="bt-mini-stat-label">
                                        {isAnnual ? 'Annual avg' : 'Monthly'}
                                    </div>
                                    <div className="bt-mini-stat-value">{formatMoney(monthlyEst)}</div>
                                </div>
                                <div className={`bt-mini-stat${overdue > 0 || pending > 0 ? ' due' : ''}`}>
                                    <div className="bt-mini-stat-label">Next Due</div>
                                    <div className="bt-mini-stat-value">{nextDueLabel}</div>
                                </div>
                            </div>

                            {/* 12-month grid OR 5-up sub-tax grid */}
                            {isAnnual ? (
                                <div className="bt-subtax-grid">
                                    {cells.map((c, i) => (
                                        <SubTaxCell
                                            key={i}
                                            tracker={tracker}
                                            row={row}
                                            index={i}
                                            cellValue={c}
                                            onClick={() => onCellClick(row, 'subTaxes', i)}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="bt-months-grid">
                                    {cells.map((c, i) => (
                                        <MonthCell
                                            key={i}
                                            label={MONTH_LABELS[i]}
                                            cellValue={c}
                                            incoming={incoming}
                                            forceNa={isQuarterly && !isQuarterlyFilingMonth(i)}
                                            paymentDay={method.day}
                                            onClick={() => onCellClick(row, 'months', i)}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * One month cell — paid/verified/unverified/pending/overdue/future/na variants.
 * Top row: tiny month + date label; middle: amount; bottom: status + icon.
 */
function MonthCell({ label, cellValue, incoming, forceNa, paymentDay, onClick }) {
    const parsed = parseCell(cellValue);
    const status = forceNa ? 'na' : getCellStatus(parsed);
    const verified = isCellFullyVerified(parsed);
    const totalPaid = getCellTotalPaid(parsed);
    const totalSales = getCellTotalSales(parsed);
    const firstDate = (parsed.payments || [])[0]?.date;

    const cls = ['bt-month-cell', status];
    if (incoming) cls.push('incoming');
    if (status === 'paid' && verified) cls.push('verified');

    if (status === 'na') {
        return (
            <div className={cls.join(' ')}>
                <div className="bt-month-cell-name">{label}</div>
                <div className="bt-month-cell-amt">N/A</div>
                <div className="bt-month-cell-status">
                    <span style={{ fontSize: 9 }}>Not a filing month</span>
                </div>
            </div>
        );
    }

    const dateSuffix = firstDate
        ? formatShortDate(firstDate)
        : (status === 'pending' || status === 'future') && paymentDay
            ? `${label} ${paymentDay}`
            : '';

    return (
        <div
            className={cls.join(' ')}
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
        >
            <div className="bt-month-cell-name">
                {label}{dateSuffix ? ` · ${dateSuffix}` : ''}
            </div>
            {status === 'future' ? (
                <div className="bt-month-cell-amt">—</div>
            ) : (
                <div className="bt-month-cell-amt">
                    {formatMoney(totalPaid || parsed.expected)}
                </div>
            )}
            <div className="bt-month-cell-status">
                <span>{statusLabel(status, verified)}</span>
                <span aria-hidden="true">{statusIcon(status, verified)}</span>
            </div>
            {totalSales > 0 && (
                <span className="bt-month-cell-sales">
                    <span role="img" aria-label="Sales reported">📊</span> {formatMoney(totalSales)}
                </span>
            )}
        </div>
    );
}

function SubTaxCell({ tracker, row, index, cellValue, onClick }) {
    const parsed = parseCell(cellValue);
    const status = getCellStatus(parsed);
    const verified = isCellFullyVerified(parsed);
    const totalPaid = getCellTotalPaid(parsed);
    const label = subTaxLabel(tracker, row, index);
    const icon = SUB_TAX_ICONS[tracker.key]?.[index] || '📑';

    const cls = ['bt-subtax-cell', status];
    if (status === 'paid' && verified) cls.push('verified');

    if (status === 'na') {
        return (
            <div className={cls.join(' ')}>
                <div className="bt-subtax-cell-icon" role="img" aria-label={label}>{icon}</div>
                <div className="bt-subtax-cell-label">{label}</div>
                <div className="bt-subtax-cell-amt">N/A</div>
                <div className="bt-subtax-cell-status">
                    <span style={{ fontSize: 9 }}>Not applicable</span>
                </div>
            </div>
        );
    }

    return (
        <div
            className={cls.join(' ')}
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
        >
            <div className="bt-subtax-cell-icon" role="img" aria-label={label}>{icon}</div>
            <div className="bt-subtax-cell-label">{label}</div>
            <div className="bt-subtax-cell-amt">
                {status === 'future' ? '—' : formatMoney(totalPaid || parsed.expected)}
            </div>
            <div className="bt-subtax-cell-status">
                <span>{statusLabel(status, verified)}</span>
                <span aria-hidden="true">{statusIcon(status, verified)}</span>
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------
// helpers
// ----------------------------------------------------------------------

function statusLabel(status, verified) {
    if (status === 'paid' && verified) return 'Verified';
    if (status === 'paid')              return 'Paid · unverified';
    if (status === 'partial')           return 'Partial';
    if (status === 'pending')           return 'Pending';
    if (status === 'overdue')           return 'Overdue';
    if (status === 'future')            return 'Upcoming';
    return '';
}
function statusIcon(status, verified) {
    if (status === 'paid' && verified) return '✓✓';
    if (status === 'paid')              return '⏱';
    if (status === 'partial')           return '½';
    if (status === 'pending')           return '!';
    if (status === 'overdue')           return '⚠';
    if (status === 'future')            return '+';
    return '';
}
function formatShortDate(iso) {
    if (!iso) return '';
    try {
        const d = new Date(`${iso}T00:00`);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
        return iso;
    }
}

function buildPaymentsStats(tracker, rows, year) {
    let paid = 0;
    let verified = 0;
    let unverified = 0;
    let pending = 0;
    let upcoming = 0;
    let ytd = 0;
    const isAnnual = tracker.frequency === 'annual';
    rows.forEach((row) => {
        const isQuarterly = tracker.key === 'salesTax' && row.fields?.filingFrequency === 'Quarterly';
        const cells = cellsForYear(row, year, isAnnual ? 'subTaxes' : 'months');
        cells.forEach((raw, i) => {
            const parsed = parseCell(raw);
            const status = effectiveStatus(parsed, isQuarterly, i);
            if (status === 'paid' || status === 'partial') {
                paid += 1;
                if (isCellFullyVerified(parsed)) verified += 1;
                else unverified += 1;
                ytd += getCellTotalPaid(parsed);
            } else if (status === 'pending' || status === 'overdue') {
                pending += 1;
            } else if (status === 'future') {
                upcoming += 1;
            }
        });
    });
    return { paid, verified, unverified, pending, upcoming, ytd };
}

function effectiveStatus(parsed, isQuarterly, monthIndex) {
    if (isQuarterly && !isQuarterlyFilingMonth(monthIndex) && parsed.type === 'future') {
        return 'na';
    }
    return getCellStatus(parsed);
}

function subTaxLabel(tracker, row, index) {
    const stored = row.subTaxLabels?.[index];
    if (stored) return stored;
    return tracker.subTaxLabels?.[index] || `Slot ${index + 1}`;
}

/** Convert a row → API payload for save (used by reactivate). */
function belongsFilterLabel(filter) {
    if (filter === 'all') return 'All';
    return BELONGS_META[filter]?.label || filter;
}

function accountToPayload(row) {
    return {
        accountId: row.accountId,
        trackerKey: row.trackerKey,
        label: row.label,
        belongs: row.belongs,
        frequency: row.frequency,
        fields: row.fields || {},
        paymentMethod: row.paymentMethod,
        subTaxLabels: row.subTaxLabels,
    };
}

export default TrackerDetail;
